"use client"

import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import { ChartBarIcon, ChartPieIcon } from "@heroicons/react/24/solid"
import { clsx } from "clsx"
import { createRef, type ReactElement, useState } from "react"
import { Bar, Bubble, Doughnut, Line, Pie, PolarArea, Radar, Scatter } from "react-chartjs-2"
import ReactMarkdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import { visit } from "unist-util-visit"
import { z } from "zod"
import {
    BubbleChartIcon,
    DoughnutChartIcon,
    LineChartIcon,
    PolarAreaChartIcon,
    RadarChartIcon,
    ScatterChartIcon,
} from "./Icons"
import styles from "./StyledText.module.css"

interface Props {
    children: string
}

const pointSchema = z.object({
    label: z.string(),
    value: z.number(),
})

const dimensionSchema = z.object({
    label: z.string(),
    points: z.array(pointSchema),
})

const possibleChartTypes = ["bar", "line", "pie", "doughnut", "radar", "polarArea", "bubble", "scatter"] as const
const scaleCharts = ["bar", "line", "bubble", "scatter"] as const

const chartSchema = z.object({
    title: z.string(),
    type: z.enum(possibleChartTypes).default("bar"),
    alternativeTypeOptions: z.array(z.enum(possibleChartTypes)).optional(),
    labels: z
        .object({
            x: z.string().optional(),
            y: z.string().optional(),
        })
        .optional(),
    dimensions: z.array(dimensionSchema),
    options: z
        .object({
            indexAxis: z.enum(["x", "y"]).optional(),
            legend: z
                .object({
                    display: z.boolean().default(true),
                    position: z.enum(["top", "left", "bottom", "right"]).default("top"),
                })
                .optional(),
            colors: z.array(z.string()).optional(),
            scales: z
                .object({
                    x: z
                        .object({
                            title: z
                                .object({
                                    display: z.boolean().default(true),
                                    text: z.string(),
                                })
                                .optional(),
                            stacked: z.boolean().optional(),
                        })
                        .optional(),
                    y: z
                        .object({
                            title: z
                                .object({
                                    display: z.boolean().default(true),
                                    text: z.string(),
                                })
                                .optional(),
                            stacked: z.boolean().optional(),
                        })
                        .optional(),
                })
                .optional(),
        })
        .optional(),
})

type Point = z.infer<typeof pointSchema>
type Dimension = z.infer<typeof dimensionSchema>

function getChartComponent(type: (typeof possibleChartTypes)[number]) {
    switch (type) {
        case "line":
            return Line
        case "pie":
            return Pie
        case "doughnut":
            return Doughnut
        case "radar":
            return Radar
        case "polarArea":
            return PolarArea
        case "bubble":
            return Bubble
        case "scatter":
            return Scatter
        case "bar":
        default:
            return Bar
    }
}

// Round using significant digits
function roundSigDig(num: number, sigDig: number) {
    const mult = Math.pow(10, sigDig - Math.floor(Math.log10(Math.abs(num))) - 1)

    if (Number.isNaN(mult) || !Number.isFinite(mult)) {
        return num
    }

    return Math.round(num * mult) / mult
}

const RemarkPlugin = () => (root: any) => {
    return visit(root, "text", (node, index, parent) => {
        const match = node.value.match(/\[CHART\s*{(?:[^{}]|\{(?:[^{}]|\{[^{}]*\})*\})*}\]/)
        if (match) {
            try {
                const chartDataString = (match[0] as string).replace("[CHART ", "").slice(0, -1).trim()
                const chartData = chartSchema.parse(JSON.parse(chartDataString))

                parent.children.splice(index, 1, {
                    type: "chart",
                    data: chartData,
                })
            } catch (error) {
                console.error(error)
            }
        }
    })
}

const RehypePlugin = () => (root: any) => {
    return visit(root, "chart", (node) => {
        node.type = "text"
        node.value = <ChartWithButtons data={node.data} />
    })
}

function transpose<T>(matrix: T[][]): T[][] {
    if (matrix.length === 0) return []
    return matrix[0].map((_, colIndex) => matrix.map((row) => row[colIndex]))
}

function removeChartCarriageReturns(text: string): string {
    function findMatchingBraceIndex(text: string, startIndex: number): number {
        const braceStack = []
        for (let i = startIndex; i < text.length; i++) {
            if (text[i] === "{") {
                braceStack.push("{")
            } else if (text[i] === "}") {
                braceStack.pop()
                if (braceStack.length === 0) {
                    return i
                }
            }
        }
        return -1
    }

    let result = ""
    let i = 0
    while (i < text.length) {
        const chartStartIndex = text.indexOf("[CHART", i)
        if (chartStartIndex === -1) {
            result += text.slice(i)
            break
        } else {
            result += text.slice(i, chartStartIndex)
            i = chartStartIndex + 6
            while (i < text.length && /\s/.test(text[i])) {
                i++
            }
            if (i < text.length && text[i] === "{") {
                const braceStartIndex = i
                const braceEndIndex = findMatchingBraceIndex(text, braceStartIndex)
                if (braceEndIndex !== -1) {
                    let j = braceEndIndex + 1
                    while (j < text.length && /\s/.test(text[j])) {
                        j++
                    }
                    if (j < text.length && text[j] === "]") {
                        const chartContent = text.slice(chartStartIndex, j + 1)
                        const cleanedChartContent = chartContent.replace(/\r?\n\s*/g, " ")
                        result += cleanedChartContent
                        i = j + 1
                        continue
                    }
                }
            }
            result += "[CHART"
        }
    }
    return result
}

function ChartWithButtons({ data }: { data: z.infer<typeof chartSchema> }) {
    const [chartType, setChartType] = useState(data.type)
    const ChartComponent = getChartComponent(chartType)

    const uniqLabels = new Set<string>()
    data.dimensions.forEach((dimension: Dimension) => {
        dimension.points.forEach((point: Point) => {
            uniqLabels.add(point.label)
        })
    })
    const labels = Array.from(uniqLabels)
    let max = 0
    const datasets = data.dimensions.map((dimension: Dimension, index: number) => {
        return {
            label: dimension.label,
            data: labels.map((label) => {
                const point = dimension.points.find((p: Point) => p.label === label)
                if (point && point.value > max) {
                    max = point.value
                }
                return point ? roundSigDig(point.value, 2) : 0
            }),
            backgroundColor: data.options?.colors ? data.options.colors[index % data.options.colors.length] : undefined,
        }
    })

    const chartTypes = [data.type, ...(data.alternativeTypeOptions || [])] as (typeof possibleChartTypes)[number][]

    const chartTypeIcons: { [key in (typeof possibleChartTypes)[number]]?: ReactElement } = {
        bar: <ChartBarIcon className="size-5" aria-hidden="true" />,
        pie: <ChartPieIcon className="size-5" aria-hidden="true" />,
        line: <LineChartIcon className="size-5" aria-hidden="true" />,
        doughnut: <DoughnutChartIcon className="size-5" aria-hidden="true" />,
        radar: <RadarChartIcon className="size-5" aria-hidden="true" />,
        polarArea: <PolarAreaChartIcon className="size-5" aria-hidden="true" />,
        bubble: <BubbleChartIcon className="size-5" aria-hidden="true" />,
        scatter: <ScatterChartIcon className="size-5" aria-hidden="true" />,
    }

    function getChartLabel(chartType: string): string {
        return chartType.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
    }

    const handleDownload = () => {
        console.log("data", data)

        import("xlsx").then((xlsx) => {
            // First column (categories)
            const categoryHeader = data.labels?.x ?? data.dimensions[0].label
            const categories = data.dimensions[0].points.map((point: Point) => point.label)

            const firstColumn = [categoryHeader, ...categories]
            const otherColumns = []

            if (data.dimensions.length > 1) {
            } else {
                otherColumns.push(["Value", ...data.dimensions[0].points.map((point: Point) => point.value)])
            }

            const workbook = xlsx.utils.book_new()
            const sheet = xlsx.utils.aoa_to_sheet(transpose([firstColumn, ...otherColumns]))

            xlsx.utils.book_append_sheet(workbook, sheet, "Data")
            xlsx.writeFile(workbook, `${data.title}.xlsx`)
        })
    }

    return (
        <div className="relative rounded-md bg-white p-4">
            {chartTypes.length > 1 && (
                <div className="absolute top-2 right-2">
                    <span className="isolate inline-flex rounded-md shadow-sm">
                        {chartTypes.map((type, index) => (
                            <button
                                key={type}
                                type="button"
                                onClick={() => setChartType(type)}
                                title={getChartLabel(type)}
                                className={clsx(
                                    "relative inline-flex items-center p-2 ring-1 ring-gray-300 ring-inset focus:z-10",
                                    type === chartType
                                        ? "bg-primary-500 text-white"
                                        : "bg-white text-gray-400 hover:bg-gray-50",
                                    index === 0 ? "rounded-l-md" : "",
                                    index === chartTypes.length - 1 ? "rounded-r-md" : "-ml-px"
                                )}
                            >
                                <span className="sr-only">{getChartLabel(type)}</span>
                                {chartTypeIcons[type] || type}
                            </button>
                        ))}
                    </span>
                </div>
            )}
            <ChartComponent
                data={{
                    labels,
                    datasets,
                }}
                options={{
                    indexAxis: data.options?.indexAxis || "x",
                    responsive: true,
                    maintainAspectRatio: true,
                    ...(scaleCharts.includes(chartType) && {
                        scales: {
                            x: {
                                title: {
                                    display: data.labels?.x ? true : false,
                                    text: data.labels?.x,
                                },
                                stacked: data.options?.scales?.x?.stacked || false,
                            },
                            y: {
                                title: {
                                    display: data.labels?.y ? true : false,
                                    text: data.labels?.y,
                                },
                                stacked: data.options?.scales?.y?.stacked || false,
                                suggestedMax: Math.ceil(max * 1.1),
                            },
                        },
                    }),
                    plugins: {
                        legend: {
                            display: data.options?.legend?.display ?? true,
                            position:
                                data.options?.legend?.position ||
                                (["pie", "doughnut", "radar", "polarArea"].includes(chartType) ? "left" : "top"),
                        },
                        title: {
                            display: true,
                            text: data.title,
                        },
                        datalabels: {
                            anchor: "end",
                            align: "end",
                            display: "auto",
                            clip: true,
                        },
                    },
                }}
                className="max-h-[50vh] w-[60rem] flex-none px-3 pr-12"
            />
            <button
                onClick={handleDownload}
                className="bg-primary-500 hover:bg-primary-600 absolute right-2 bottom-2 rounded px-3 py-1 text-white"
                title="Download Chart"
            >
                <ArrowDownTrayIcon className="size-5" />
            </button>
        </div>
    )
}

export function StyledText({ children }: Props) {
    return (
        <ReactMarkdown
            className={clsx(
                "prose max-w-none space-y-4 px-5 text-gray-900 dark:prose-invert dark:text-gray-100",
                styles.markdown
            )}
            components={{
                h1: ({ node: _node, ...props }) => <h1 className="text-3xl font-bold lg:text-4xl" {...props} />,
                h2: ({ node: _node, ...props }) => <h2 className="text-xl font-bold lg:text-2xl" {...props} />,
                h3: ({ node: _node, ...props }) => <h3 className="text-lg font-bold" {...props} />,
                h4: ({ node: _node, ...props }) => <h4 className="text-base font-bold" {...props} />,
                h5: ({ node: _node, ...props }) => <h5 className="text-base font-semibold" {...props} />,
                ol: ({ node: _node, ...props }) => <ol className="ml-4 list-inside list-decimal" {...props} />,
                ul: ({ node: _node, ...props }) => <ul className="ml-4 list-outside list-disc" {...props} />,
                li: ({ node: _node, ...props }) => <li className="mb-1 ml-6" {...props} />,
                table: ({ node: _node, children, ...props }) => {
                    const ref = createRef<HTMLTableElement>()

                    return (
                        <table
                            className="table-auto overflow-hidden rounded-md bg-white dark:bg-zinc-900"
                            ref={ref}
                            {...props}
                        >
                            <>{children}</>
                            <tfoot>
                                <tr>
                                    <td colSpan={100} className="relative h-8">
                                        <button
                                            onClick={() => {
                                                import("xlsx").then((xlsx) => {
                                                    const workbook = xlsx.utils.book_new()
                                                    const sheet = xlsx.utils.table_to_sheet(ref.current)
                                                    xlsx.utils.book_append_sheet(workbook, sheet, "Data")
                                                    xlsx.writeFile(workbook, "table.xlsx")
                                                })
                                            }}
                                            className="bg-primary-500 hover:bg-primary-600 absolute right-2 bottom-2 rounded px-3 py-1 text-white"
                                            title="Download Chart"
                                        >
                                            <ArrowDownTrayIcon className="size-5" />
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )
                },
                thead: ({ node: _node, ...props }) => (
                    <thead className="rounded-t-md bg-gray-100 dark:bg-zinc-800" {...props} />
                ),
                th: ({ node: _node, ...props }) => <th className="px-4 py-2 text-gray-900 dark:text-gray-100" {...props} />,
                td: ({ node: _node, ...props }) => <td className="px-4 py-2 text-gray-900 dark:text-gray-100" {...props} />,
                tbody: ({ node: _node, ...props }) => (
                    <tbody className="divide-y divide-gray-200 rounded-b-md dark:divide-gray-700" {...props} />
                ),
            }}
            remarkPlugins={[RemarkPlugin, remarkGfm, remarkMath]}
            rehypePlugins={[RehypePlugin, rehypeKatex]}
            remarkRehypeOptions={{
                passThrough: ["chart" as any],
            }}
        >
            {removeChartCarriageReturns(children)}
        </ReactMarkdown>
    )
}
